import React from 'react';
import { Box, Container, makeStyles } from '@material-ui/core';
import { RouteComponentProps, useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';
import SectionHeading from 'components/SectionHeading';
import ProductDetailedButton from '../DetailedButton/DetailedButton';
import { groupByCategory } from '../../utils';
import { sendEvent } from '../../../utils/google-analytics';
import { Product, ProductCategory } from '../../../types';
import i18n from 'i18n';
import Steps from '../Steps/Steps';
import FAQSection from '../../../Homepage/components/FAQSection/FAQSection';
const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(10),
  },
  list: {
    listStyle: 'none',
    padding: theme.spacing(0, 0.55),
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1),
    maxWidth: 900,
    alignItems: 'stretch',
    boxSizing: 'border-box',
    [theme.breakpoints.down('lg')]: {
      maxWidth: 640,
      gap: theme.spacing(0.75),
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      maxWidth: 480,
      gap: theme.spacing(0.75),
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      maxWidth: 320,
      gap: theme.spacing(0.75),
    },
  },
  listItem: {
    display: 'flex',
    justifyContent: 'center',
    minWidth: 0,
    '& button': {
      width: '100%',
      maxWidth: 180,
      height: '100%',
      overflow: 'hidden',
      [theme.breakpoints.up('sm')]: {
        maxWidth: 200,
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: 220,
      },
      [theme.breakpoints.up('lg')]: {
        maxWidth: 240,
      },
      [theme.breakpoints.up('xl')]: {
        maxWidth: 260,
      },
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: theme.spacing(0.55),
      background: '#f9fafb',
      border: '1px solid rgba(51, 51, 51, 0.25)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      padding: theme.spacing(1.9),
      [theme.breakpoints.up('sm')]: {
        gap: theme.spacing(0.85),
      },
      [theme.breakpoints.up('md')]: {
        gap: theme.spacing(1.1),
      },
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        margin: 0,
        width: '100%',
      },
      '& > div:last-of-type': {
        marginLeft: 0,
        marginRight: 0,
        paddingRight: 0,
      },
      '& .image': {
        padding: 0,
      },
      '& .MuiTypography-h5': {
        marginBottom: 0,
        display: 'block',
        width: '100%',
        textAlign: 'center',
        fontFamily: theme.typography.fontFamily,
        textTransform: 'uppercase',
        fontWeight: 800,
        fontSize: '0.875rem',
        letterSpacing: '0.06em',
        [theme.breakpoints.up('sm')]: {
          fontSize: '1rem',
        },
        [theme.breakpoints.up('md')]: {
          fontSize: '1.0625rem',
        },
        [theme.breakpoints.up('lg')]: {
          fontSize: '1.125rem',
        },
      },
      '& img, & svg': {
        width: '8.5rem',
        height: '8.5rem',
        flexShrink: 0,
        objectFit: 'contain',
        [theme.breakpoints.up('sm')]: {
          width: '8.5rem',
          height: '8.5rem',
        },
        [theme.breakpoints.up('md')]: {
          width: '9.2rem',
          height: '9.2rem',
        },
        [theme.breakpoints.up('lg')]: {
          width: '10rem',
          height: '10rem',
        },
        [theme.breakpoints.up('xl')]: {
          width: '10.8rem',
          height: '10.8rem',
        },
      },
      '& svg': {
        fill: `${theme.palette.secondary.main} !important`,
      },
      '& svg *': {
        fill: `${theme.palette.secondary.main} !important`,
      },
    },
  },
}));

interface ProductCategoryStepProps extends RouteComponentProps {
  products: Product[];
}

export default function ProductCategoryStep({ products }: ProductCategoryStepProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headingHtmlId = 'ProductCategoryStep-heading';
  const categories = groupByCategory(products);

  const onCategoryClick = (x: ProductCategory) => {
    sendEvent('repair', `category_selected-name_${x.title.de}`, x.title.de);
    navigate(`./${x.seo?.[i18n.language]}`);
  };

  function renderButtons() {
    const buttons = categories.map((x, i) => (
      <Box component="li" key={i} className={classes.listItem}>
        <ProductDetailedButton
          onClick={() => onCategoryClick(x)}
          name={x.title}
          imageUrl={x.imageUrl}
        />
      </Box>
    ));
    return buttons;
  }

  return (
    <Box className={classes.root}>
      <Container maxWidth="xl">
        <Steps currentStep={1} />

        <Box mb={1} />
        <SectionHeading id={headingHtmlId} component="h1" size="large">
          {t('Choose the item to repair')}
        </SectionHeading>
        <Box mb={6} />
        <ul aria-labelledby={headingHtmlId} className={classes.list}>
          {renderButtons()}
        </ul>
      </Container>
      <FAQSection variant="product" />
    </Box>
  );
}
